import cls from "classnames";
import { useState, useEffect, Fragment } from "react";
import * as Sentry from "@sentry/nextjs";

import useSessionStore from "../../../store/sessionStore";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { api } from "@qoohoo/common/constants/analytics";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";
import { getPageNameByUrl } from "@qoohoo/common/utils/analytics";
import httpf from "../../../utils/frontend/httpf";

const modal_name = "custom_user_form_modal";

const CustomUserFormModal = ({
  onClose,
  onSubmitCustomUserForm,
  formConfig,
  channelDetails
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const userData = useSessionStore((state) => state.userData);
  const channelId = channelDetails.uid;

  let current_screen = "";
  if(typeof window !== "undefined"){
    current_screen = getPageNameByUrl(window.location.href);
  }

  const trackDetails = {
    current_user: userData?.username,
    current_screen: current_screen,
    modal_name,
    channel_id: channelId,
    user_id: userData.uid,
  };

  function isFormValid() {
    let isFormValid = true;

    const hasErrors = Object.keys(formErrors).length > 0;
    if (hasErrors) {
      isFormValid = false;
    } else {
      for (let i=0; i< formConfig.order.length; i++) {
        const name = formConfig.order[i];
        const fieldValue = formData[name];
        const fieldConfig = formConfig.fields[name];

        if (fieldConfig.required && !fieldValue) {
          isFormValid = false;
          break;
        }
      }
    }

    return isFormValid;
  }

  function validateForm() {
    let moreFormErrors = {};

    for (let i=0; i< formConfig.order.length; i++) {
      const name = formConfig.order[i];
      const fieldValue = formData[name];
      const fieldConfig = formConfig.fields[name];

      let isFieldValid;

      if (formErrors[name]) {
        continue;
      }

      // Note: It's ok to skip if not required && value is empty
      if (fieldConfig.required || fieldValue) {
        isFieldValid = fieldConfig.validator(fieldValue);

        if (!isFieldValid) {
          moreFormErrors[name] = {
            isBlur: true,
            description: `Enter a valid ${fieldConfig.label}`
          }
        }
      }
    }

    const newFormErrors = {
      ...formErrors,
      ...moreFormErrors
    };

    setFormErrors(newFormErrors);

    const isFormValid = Object.keys(newFormErrors).length === 0;

    return isFormValid;
  }

  const onSubmitAPI = async () => {
    setIsLoading(true);

    try {
      const payload = formConfig.api.user_form.preparePayload(formData, userData, channelDetails);
      console.log('user_form..', payload);

      if (formConfig.api.user_form.method === 'POST') {
        await httpf.post(formConfig.api.user_form.url, payload);
      } else {
        // TODO: Handle GET later
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      Track.customEvent(api.custom_user_form_api, {
        type: "failed",
        ...trackDetails
      });

      throw error;
    }
  };

  const handleClose = () => {
    Track.customEvent(modal_name, {
      actionType: "modal_close",
      label: modal_name,
      ...trackDetails,
    });

    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if(isLoading) {
      return;
    }

    try {
      const isFormValid = validateForm();

      if (!isFormValid) {
        return false;
      }

      await onSubmitAPI();

      Track.customEvent(api.custom_user_form_api , {
        type: "success",
        ...trackDetails
      });

      onSubmitCustomUserForm();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");

      console.error(error);
      Sentry.captureException(error);
    }
  };

  function handleOnBlur(e) {
    e.preventDefault();
    const name = e.target.name;

    const error = formErrors[name];

    if(error) {
      const newFormErrors = {
        ...formErrors,
        [name]: {
          isBlur: true,
          description: error.description
        }
      }

      setFormErrors(newFormErrors);
    }
  }

  function handleOnChange(e) {
    const name = e.target.name;
    const value = e.target.value?.replace(/\s\s+/g, " ");

    const fieldConfig = formConfig.fields[name];

    const isValid = fieldConfig.validator(value);

    const newFormData = {
      ...formData,
      [name]: fieldConfig.formatValue(value)
    }

    setFormData(newFormData);

    if (isValid) {
      const newFormErrors = {
        ...formErrors,
      }

      delete newFormErrors[name];

      setFormErrors(newFormErrors);
    } else {
      const error = formErrors[name];

      // Skip if already set
      if(!error) {
        const newFormErrors = {
          ...formErrors,
          [name]: {
            description: `Enter a valid ${fieldConfig.label}`
          }
        }

        setFormErrors(newFormErrors);
      }
    }
  }

  function renderFields() {
    const fields = formConfig.order.map((name, index) => {
      const field = formConfig.fields[name];

      const error = formErrors[name];

      return (
        <div key={field.name} className="">
          <input
            type={field.type}
            name={field.name}
            placeholder={field.placeholder}
            value={formData[name] || ''}
            onChange={handleOnChange}
            className={cls(
              "w-full text-[#22272B] text-xl pb-1 px-3 leading-8 bg-white active:bg-white placeholder-[#22272B]",
              "placeholder-opacity-20 border-b border-[#A7A9AA] outline-none focus:outline-none",
              "mt-8"
            )}
            autoComplete={field.autoComplete || "true"}
            required={field.required || false}
            disabled={isLoading}
            autoFocus={index===0}
            onBlur={handleOnBlur}
          />
          {
            error && error.isBlur ? (
              <div className="mt-2 px-3 text-sm text-red-500 text-left">{error.description}</div>
            ) : null
          }

        </div>
      );
    });

    return fields;
  }

  useEffect(()=>{
  Track.customEvent(modal_name, {
      actionType: "modal_open",
      label: modal_name,
      ...trackDetails,
    });
  }, []);

  const isSubmitDisabled = isLoading || !isFormValid();

  return (
    <Transition
      as={Fragment}
      show={true}
      appear={true}
      enter="ease-out duration-150"
      enterFrom="scale-95"
      enterTo="scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="sm:inline-block sm:align-middle h-screen sm:h-full">
        <div className="relative inline-flex items-end justify-center w-full h-full sm:items-center font-nunito sm:py-14">
          <div className="relative bg-white sm:rounded-sm max-w-full w-full h-full sm:h-[526px] sm:max-w-[344px] sm:w-[344px]">
            {onClose && (
              <img
                src="/assets/icons/cancel.svg"
                lt="close button"
                onClick={handleClose}
                className="absolute z-10 cursor-pointer select-none right-5 top-6"
              />
            )}
            <div className="flex flex-col h-full w-full relative">
              <form
                id="update-profile-form"
                className="mt-11"
                onSubmit={handleSubmit}
              >
                <div className="relative px-5 flex flex-col flex-auto w-full min-h-0 overflow-hidden">
                  <div className="font-semibold text-xl text-[#22272B] leading-6 text-left font-nunito flex-shrink-0">
                    {formConfig.title}
                  </div>
                  <div className="font-nunito text-sm leading-6 text-[#000000] flex-auto min-h-0 mt-2 overflow-y-scroll hide-scrollbar pb-20">
                      {renderFields()}
                  </div>
                </div>
                <div className="fixed sm:absolute left-0 bottom-0 flex-shrink-0 w-full flex items-center justify-center px-5 pt-3 pb-8 sm:pb-4 gradient-fade-white border-t border-[#22272B] border-opacity-20">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className={cls("bg-[#605FE7] disabled:cursor-not-allowed disabled:opacity-50 hover:bg-opacity-90 px-4" +
                      " py-[10px]" +
                      " w-10/12" +
                      " h-[40px] rounded-full overflow-hidden cursor-pointer flex items-center justify-center font-semibold text-[14px] leading-5 text-center",
                      {"opacity-40 cursor-not-allowed" : isLoading},
                    )}
                  >
                    {isLoading ? <Spinner className="!h-4 !w-4" /> : (
                      <div className="flex flex-row w-full pl-5">
                        <span className="w-full">
                          Proceed to pay
                        </span>

                        <img
                          src="/assets/icons/arrow-right.svg"
                          alt="arrow right"
                          className="w-5 group-hover:hidden"
                        />
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default CustomUserFormModal;
